import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSelector } from './LanguageSelector';
import WiseRankLogo from '../assets/logos/wiserank-logo.svg';

export const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  React.useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent scroll when mobile menu is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Helper function to handle navigation
  const handleNavigation = (href: string) => {
    if (href.startsWith('/#')) {
      // If we're not on the home page and trying to navigate to a section
      if (location.pathname !== '/') {
        // Navigate to home page with the hash
        window.location.href = href;
      } else {
        // If we're already on home page, just scroll to the section
        const element = document.getElementById(href.substring(2));
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navItems = {
    features: t('nav.features'),
    pricing: t('nav.pricing')
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
    open: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <nav className="fixed w-full bg-white/80 dark:bg-black/80 backdrop-blur-md z-50 border-b border-gray-200/20 dark:border-gray-700/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img 
                src={WiseRankLogo} 
                alt="WiseRank.ai" 
                className="h-8 w-auto dark:invert transition-transform duration-200 hover:scale-105" 
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {Object.entries(navItems).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleNavigation(`/#${key}`)}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 text-sm font-medium hover-underline"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-6">
            <a
              href="tel:+33744958400"
              className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm font-medium">+33 7 44 95 84 00</span>
            </a>
            <LanguageSelector />
            <ThemeToggle />
            <motion.a
              href="https://calendly.com/alex-craft-with-ai/demo-de-craftseo?month=2025-01"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 rounded-full premium-gradient text-white font-medium text-sm shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30 transition-shadow"
            >
              {t('hero.cta')}
            </motion.a>
            <a
              href="https://app.wiserank.ai/login"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors hover-underline"
            >
              Sign In
            </a>
            <a
              href="https://app.wiserank.ai/register"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors hover-underline"
            >
              Sign Up
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center space-x-4 md:hidden">
            <a
              href="tel:+33744958400"
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <Phone className="w-5 h-5" />
            </a>
            <LanguageSelector />
            <ThemeToggle />
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            className="md:hidden overflow-hidden bg-white dark:bg-black border-t border-gray-200 dark:border-gray-800"
          >
            <div className="px-4 py-6 space-y-4">
              {Object.entries(navItems).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => handleNavigation(`/#${key}`)}
                  className="block w-full text-left py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {label}
                </button>
              ))}
              <motion.a
                href="https://calendly.com/alex-craft-with-ai/demo-de-craftseo?month=2025-01"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="block w-full mt-6 px-4 py-3 rounded-full premium-gradient text-white font-medium text-center shadow-lg shadow-primary-500/20"
              >
                {t('hero.cta')}
              </motion.a>
              <a
                href="https://app.wiserank.ai/login"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-left py-2 text-base text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign In
              </a>
              <a
                href="https://app.wiserank.ai/register"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-left py-2 text-base text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign Up
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}